import React from 'react'
import { Link } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, Clock, FileText, Save } from 'lucide-react';
import User from "../../../models/User";
import Page from '../../Page'
import { formatMinutes, getJourneyProgress } from '../components/applicationProgress';
import ApplicationStepper from '../components/ApplicationStepper';

/** First-visit overview row: just the section name and its time. */
const PlainSectionRow = ({ section }) => (
    <li className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 md:px-4 md:py-3">
        <span className="grow shrink basis-0 min-w-0 truncate text-[13px] md:text-[15px] font-medium text-gray-900">
            {section.title}
        </span>
        <span className="text-[11px] md:text-xs whitespace-nowrap tabular-nums text-gray-400">~{section.minutes} min</span>
    </li>
);

const SectionRow = ({ section, isNext }) => (
    <li className={TB.mapClass('flex items-center gap-2.5 md:gap-3 rounded-lg border px-3 py-1.5 md:px-4 md:py-3', {
        'border-secondary bg-secondary/5': isNext,
        'border-gray-200 bg-white': !isNext,
    })}>
        {section.completed ? (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
            </span>
        ) : (
            <span className={TB.mapClass('h-5 w-5 shrink-0 rounded-full border-2', {
                'border-secondary': isNext,
                'border-gray-300': !isNext,
            })} />
        )}

        <span className={TB.mapClass('grow shrink basis-0 min-w-0 truncate text-[13px] md:text-sm', {
            'text-gray-500': section.completed,
            'font-semibold text-gray-900': isNext,
            'text-gray-800': !section.completed && !isNext,
        })}>
            {section.title}
        </span>

        {isNext && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary whitespace-nowrap">
                Up next
            </span>
        )}
        <span className={TB.mapClass('text-[11px] md:text-xs whitespace-nowrap tabular-nums', {
            'text-success font-medium': section.completed,
            'text-amber-600 font-medium': section.needsConfirm,
            'text-gray-400': !section.completed && !section.needsConfirm,
        })}>
            {section.completed ? 'Done' : (section.needsConfirm ? 'Confirm' : `~${section.minutes} min`)}
        </span>
    </li>
);

const SectionGroup = ({ title, sections, nextSection, plain = false }) => {
    if (!sections.length) return null;
    return (
        <div>
            <div className="flex items-baseline justify-between px-1 pb-2">
                <h3 className="m-0 text-[11px] md:text-xs font-bold uppercase tracking-wider text-gray-500">{title}</h3>
            </div>
            <ul className="m-0 p-0 list-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                {sections.map(section => plain ? (
                    <PlainSectionRow key={section.path} section={section} />
                ) : (
                    <SectionRow
                        key={section.path}
                        section={section}
                        isNext={nextSection?.path === section.path}
                    />
                ))}
            </ul>
        </div>
    );
};

export default class ApplicationStartStep extends Page {

    static path = '/get-started';

    /**
     * The title shown on the form
     */
    static StepTitle = false;

    /**
     * The step needed before editing
     */
    static StepNeedsValidation(user) {
        if (!user.validateInfo()) return this;
        return false;
    };

    /**
     * Hide completion icon on sidebar
     */
    static NeedsCompletion = () => false;

    static hasCompleted(user) {
        return true;
    }

    /**
     * Check if we should prevent application from saving and show alert
     */
    static async getValidProps(user) {
        return {}
    };

    static getPath(prefix = '/talent/application') {
        return `${prefix}${this.path}`
    }

    constructor(props) {
        super(props);
    }

    /**
     * The fields mapping for the form
     */
    static showFormButtons() {
        return false
    }

    /**
     * Render the form
     *
     * @returns {Form}
     */
    render() {
        const user = this.props.user;
        const steps = [...(user.getApplicationStepsForTalent?.() || [])];
        const progress = getJourneyProgress(user, steps);
        const isReady = !!user.hasCompletedApplication?.() && !progress.requiredRemaining.length;
        const justSaved = !!this.props.location?.state?.saved || !!window.history?.state?.usr?.saved;
        // Some sections (e.g. Worker Classification) can be complete by default, so
        // treat the user as "returning" once Basic Information has been saved.
        const isReturning = justSaved || !this.constructor.StepNeedsValidation(user);

        // Where "continue" should take a returning user (same logic used for login redirects)
        // If a section still needs confirming (e.g. Worker Classification) and it
        // comes before the normal resume point, send them there first.
        let resumeStep = user.currentApplicationStep?.();
        const confirmStep = progress.requiredRemaining.find(s => s.needsConfirm)?.step;
        if (!isReady && confirmStep && (steps.indexOf(resumeStep) === -1 || steps.indexOf(confirmStep) < steps.indexOf(resumeStep))
            && progress.nextSection?.step === confirmStep) {
            resumeStep = confirmStep;
        }
        const resumeIsStart = !resumeStep || resumeStep === this.constructor;
        const resumeTitle = isReady ? 'Review & submit' : (progress.nextSection?.title || resumeStep?.StepTitle);

        const ctaLabel = !isReturning
            ? 'Start application'
            : (isReady ? 'Review & submit' : `Continue${resumeTitle ? `: ${resumeTitle}` : ''}`);

        const ctaClass = 'btn btn-primary inline-flex items-center justify-center gap-2 px-6 w-full sm:w-auto';
        const cta = (!isReturning || resumeIsStart) ? (
            <button type="button" className={ctaClass} onClick={() => this.props.onSubmit()}>
                {ctaLabel} <ArrowRight className="h-4 w-4" />
            </button>
        ) : (
            <Link to={resumeStep.getPath()} className={`${ctaClass} hover:no-underline`}>
                {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
        );

        return (
            <div className="w-full max-w-6xl mx-auto px-3 py-4 md:px-6 md:py-10">
              <div className="rounded-xl md:rounded-2xl bg-white shadow-sm px-4 py-5 md:px-12 md:py-10">

                <div className="hidden md:block pb-8 mb-8 border-b border-gray-100">
                    <ApplicationStepper />
                </div>

                {justSaved && (
                    <div className="mb-5 md:mb-8 flex items-start gap-3 rounded-lg border border-success/30 bg-success/10 px-3 py-2.5 md:px-4 md:py-3">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 md:h-5 md:w-5 shrink-0 text-success" />
                        <div className="text-xs md:text-sm text-gray-700">
                            <p className="m-0 font-semibold text-gray-900">Your progress is saved</p>
                            <p className="m-0">You can close this page any time. When you come back, you&apos;ll pick up right here.</p>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="text-center">
                    <p className="m-0 text-[11px] md:text-xs font-bold uppercase tracking-wider text-secondary">
                        <span className="md:hidden">Step 2 of 4 &middot; </span>Talent application
                    </p>
                    <h2 className="mt-2 mb-0 text-xl leading-tight md:text-3xl font-bold text-gray-900">
                        {justSaved
                            ? `Here's where you left off, ${user.first_name}`
                            : isReturning
                            ? `Welcome back, ${user.first_name}`
                            : `Hi ${user.first_name}, let's get you on Wripple`}
                    </h2>
                    <p className="mt-2 md:mt-3 mb-0 text-sm md:text-base text-gray-600">
                        {isReady ? (
                            'Every required section is done. Give it a quick review and submit.'
                        ) : isReturning ? (
                            <>
                                You&apos;ve finished <b className="text-gray-900">{progress.requiredDoneCount} of {progress.requiredCount}</b> required
                                sections &middot; about <b className="text-gray-900">{formatMinutes(progress.minutesLeft)}</b> left.
                            </>
                        ) : (
                            <>
                                It takes about <b className="text-gray-900">{formatMinutes(progress.totalMinutes)}</b>.
                                You can stop and come back any time.
                            </>
                        )}
                    </p>
                </div>

                {/* Progress (returning) or quick facts (new) */}
                {isReturning ? (
                    <div className="mt-5 md:mt-8 mx-auto max-w-2xl flex items-center gap-3">
                        <div className="h-2 grow shrink basis-0 overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-success transition-all"
                                style={{ width: `${progress.percent}%` }}
                            />
                        </div>
                        <span className="text-xs font-semibold text-gray-500 tabular-nums">{progress.percent}%</span>
                    </div>
                ) : (
                    <div className="mt-5 md:mt-8 grid grid-cols-1 sm:grid-cols-3 sm:gap-4 rounded-lg sm:rounded-none bg-gray-50 sm:bg-transparent divide-y divide-gray-200/70 sm:divide-y-0">
                        {[
                            { icon: Clock, title: `${progress.requiredCount} short sections`, text: 'Most take about a minute' },
                            { icon: Save, title: 'Saved as you go', text: 'Each section saves when you continue' },
                            { icon: FileText, title: 'Resume handy?', text: 'It helps you fill things in faster' },
                        ].map(({ icon: Icon, title, text }) => (
                            <div key={title} className="flex items-start gap-3 sm:rounded-lg sm:bg-gray-50 px-3 py-2.5 md:px-5 md:py-4">
                                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                                <div>
                                    <p className="m-0 text-[13px] md:text-sm font-semibold text-gray-900">{title}</p>
                                    <p className="m-0 text-[11px] md:text-xs text-gray-500">{text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Sections */}
                <div className="mt-6 md:mt-10 flex flex-col gap-5 md:gap-6">
                    {isReturning ? (
                        <>
                            <SectionGroup
                                title={`Required · ${progress.requiredCount} sections`}
                                sections={progress.required}
                                nextSection={!isReady ? progress.nextSection : null}
                            />
                            <SectionGroup
                                title="Optional · helps you stand out"
                                sections={progress.optional}
                                nextSection={null}
                            />
                        </>
                    ) : (
                        <SectionGroup plain title={`${progress.requiredCount} Sections`} sections={progress.required} />
                    )}
                </div>

                {/* Action */}
                <div className="mt-6 md:mt-10 flex flex-col items-center gap-2 md:gap-3 text-center">
                    {cta}
                    <p className="m-0 text-[11px] md:text-xs text-gray-500">
                        After you submit, Wripple reviews your application within 24–72 hours.
                    </p>
                    <button
                        type="button"
                        className="btn btn-link !no-underline !px-0 mt-2 text-sm text-gray-500 hover:text-gray-900"
                        disabled={this.state?.isLoggingOut}
                        onClick={async () => {
                            this.setState({ isLoggingOut: true });
                            return TB.auth().logout();
                        }}
                    >
                        Log out
                    </button>
                </div>
              </div>
            </div>
        );
    }
}